import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import ForgotPasswordModal from './Modal/ForgotPasswordModal';
import ResetPasswordModal from './Modal/ResetPasswordModal';

const Login = () => {
  // States for login inputs
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false); // success/fail message color
  const [loading, setLoading] = useState(false); // spinner state

  // States for forgot/reset password modals
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  const navigate = useNavigate();

  // Handle login form submit
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsSuccess(false);
    setMessage('');

    try {
      // Call login API
      const response = await api.post(
        '/login',
        new URLSearchParams({ email, password }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );

      const { token, user } = response.data;

      // Save login info in session
      sessionStorage.setItem('auth_token', token);
      sessionStorage.setItem('user_id', user.id);
      sessionStorage.setItem('Module', user.module_name);
      sessionStorage.setItem('Role_Name', user.role.name);
      sessionStorage.setItem('Role_id', user.role_id);
      sessionStorage.setItem('Medical_Center_Id', user.medical_center_id);
      sessionStorage.setItem('User_Name', user.first_name);

      if (
        user.role?.name === 'Hospital Representative (Nurse)' ||
        user.role?.name === 'EMS Resource Manager'
      ) {
        sessionStorage.setItem('Medical_Center_Name', user.medical_center?.name || '');
      }

      // Save checklist_type from checklist_reminders if available
      if (user.checklist_reminders && Array.isArray(user.checklist_reminders) && user.checklist_reminders.length > 0) {
        // Use the first item's checklist_type or adjust logic as needed (e.g., find specific one based on assign_checklist)
        const checklistType = user.checklist_reminders[0].checklist_type;
        sessionStorage.setItem('checklist_type', checklistType);
        // For the provided data: this would set 'equipment'
      }

      setMessage('Login successful');
      setIsSuccess(true);

      // Redirect after short delay
      setTimeout(() => {
        switch (user.role?.name) {
          case 'Admin':
            navigate('ems/dashboard');
            break;
          case 'EMS Resource Manager':
            navigate('/ems/user-management');
            break;
          case 'EMS Requester':
            navigate('/ems/dashboard');
            break;
          case 'EMS Team':
            navigate('/ems/requests');
            break;
          case 'Hospital Representative (Nurse)':
            navigate('/ems/hospital-new-cases');
            break;
          case 'Hospital Representative (Doctor)':
            navigate('/ems/transfer-cases');
            break;
          default:
            // fallback if no role matches
            if (user.module_name === 'EMS') navigate('/ems/dashboard');
            else if (user.module_name === 'ROS') navigate('/ros/dashboard');
            else navigate('/');
            break;
        }
      }, 1000);

    } catch (error) {
      console.error('Login error:', error);
      setMessage('Login failed. Please check credentials.');
      setIsSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          .login-wrap{overflow: hidden;}
          .login-wrap {background: none;}
          .login-bg {background: url(/assets/images/backgrounds/EMS-login-bg.png) no-repeat !important; background-size: cover !important;
          background-position: center !important; justify-content: center !important;
          height: 100% !important;
          text-align: center;
          }
          .form-control::placeholder { color: black; opacity: 1;}
          .login-bg img {max-width: 413px; width: 80%;}

          ::placeholder {  color: #5a6a85 !important; }

          @media (max-width: 991px) {
            .login-wrap {background: url(/assets/images/backgrounds/EMS-login-bg.png) no-repeat !important; background-size: cover !important;
            background-position: center !important;}
          }
        `}
      </style>

      <div className="login-wrap">
        <div className="row">
          <div className="col-lg-6 p-5">
            <div className="inner-login-panel">
              <h1 className="fs-10 fw-semibold ">Welcome Back! Please login</h1>
              <p className="mb-5">Enter your credentials to access your dashboard.</p>

              {/* Login form */}
              <form onSubmit={handleLogin}>
                {/* email */}
                <div className="mb-4">
                  <label>Email</label>
                  <input
                    type="text"
                    className="form-control "
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                {/* password */}
                <div className="mb-4">
                  <label>Password</label>
                  <input
                    type="password"
                    className="form-control "
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />

                  {/* Forgot password link */}
                  <p className="mt-2">
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() => setShowForgotModal(true)} // open forgot modal
                    >
                      Forgot Password?
                    </button>
                  </p>
                </div>

                {/* remember me */}
                <div className="mb-4">
                  <div className="form-check mr-sm-2">
                    <input type="checkbox" className="form-check-input" id="sf6" value="check" />
                    <label className="form-check-label" htmlFor="sf6">Remember Me</label>
                  </div>
                </div>

                {/* submit */}
                <button
                  type="submit"
                  className="btn mb-1 waves-effect waves-light btn-primary px-5 py-2 fs-4"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  ) : null}
                  {loading ? 'Logging in...' : 'Submit'}
                </button>
              </form>

              {/* show error/success message */}
              {message && (
                <p className={`mt-3 ${isSuccess ? 'text-success' : 'text-danger'}`}>
                  {message}
                </p>
              )}
            </div>
          </div>

          <div className="col-lg-6 h-100 d-lg-block d-none">
            <div className="login-bg d-flex justify-content-center align-items-center h-100">
              <img 
                src="/assets/images/backgrounds/new-ems-logo.png" 
                alt="Login Logo" 
                className="img-fluid"
              />
            </div>
          </div>
        </div>

        {/* Forgot password modal */}
        <ForgotPasswordModal
          show={showForgotModal}
          onClose={() => setShowForgotModal(false)}
          onNext={() => {
            setShowForgotModal(false);
            setShowResetModal(true);
          }}
        />

        {/* Reset password modal */}
        <ResetPasswordModal
          show={showResetModal}
          onClose={() => setShowResetModal(false)}
        />
      </div>
    </>
  );
};

export default Login;